import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './PatientList.css';

export default function PatientList() {
    const [patients, setPatients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deleteId, setDeleteId] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [openDropdown, setOpenDropdown] = useState(null);
    const navigate = useNavigate();
    axios.defaults.withCredentials = true;
    const API_URL = process.env.REACT_APP_API_URL;
        const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(20);
    const [total, setTotal] = useState(0);
    const [lastPage, setLastPage] = useState(1);

    const handleSearchInputChange = async (e) => {
        const value = e.target.value;
        setSearchQuery(value);
        setPage(1);
        try {
                                    setIsSearching(true);
            const params = new URLSearchParams();
            params.append('search', value);
            params.append('page', '1');
            params.append('per_page', String(perPage));
            const response = await axios.get(`${API_URL}search-patients?${params.toString()}`, {
                headers: { 'Accept': 'application/json' }
            });
            let payload = response.data;
            let list = Array.isArray(payload) ? payload : (Array.isArray(payload.data) ? payload.data : []);
            setPatients(list);
            if (!Array.isArray(payload)) {
                setTotal(typeof payload.total === 'number' ? payload.total : list.length);
                setLastPage(typeof payload.last_page === 'number' ? payload.last_page : 1);
            } else {
                setTotal(list.length);
                setLastPage(1);
            }
            setError('');
        } catch (error) {
            console.error('Error searching patients:', error);
            setError(`Search error: ${error.response?.data?.error || error.message}`);
            setPatients([]);
        } finally {
            setIsSearching(false);
        }
    };

    const [isSearching, setIsSearching] = useState(false);
    const hasFetchedInitialData = useRef(false);
    const initialDateSet = useRef(false);

    const fetchPatients = useCallback(async (isSearchAction = false) => {
        try {
                                    
            console.log('fetchPatients called with:', { isSearchAction, searchQuery });
            
            // Only show loading for search actions or initial load
            if (isSearchAction || !hasFetchedInitialData.current) {
                console.log('Setting loading to true');
                setLoading(true);
            }
            
            let response;
            
            // If we have a search query, use the search-patients endpoint
            if (searchQuery) {
                const params = new URLSearchParams();
                params.append('search', searchQuery);
                params.append('page', String(page));
                params.append('per_page', String(perPage));
                response = await axios.get(`${API_URL}search-patients?${params.toString()}`, {
                    headers: { 'Accept': 'application/json' }
                });
            } 
            // If no search, get all patients
            else {
                const params = new URLSearchParams();
                params.append('page', String(page));
                params.append('per_page', String(perPage));
                response = await axios.get(`${API_URL}patients?${params.toString()}`, {
                    headers: { 'Accept': 'application/json' }
                });
            }
            
            let payload = response.data;
            let list = Array.isArray(payload) ? payload : (Array.isArray(payload.data) ? payload.data : []);
            setPatients(list);
            if (!Array.isArray(payload)) {
                setTotal(typeof payload.total === 'number' ? payload.total : list.length);
                setLastPage(typeof payload.last_page === 'number' ? payload.last_page : 1);
            } else {
                setTotal(list.length);
                setLastPage(1);
                if (!searchQuery) setPage(1);
            }
            setError('');
        } catch (error) {
            console.error('Error fetching patients:', error);
            setError(`Failed to fetch patients: ${error.response?.data?.error || error.message}`);
            setPatients([]);
        } finally {
            setLoading(false);
            setIsSearching(false);
        }
    }, [searchQuery, API_URL, page, perPage]);

    // Handle initial load and data fetching
    useEffect(() => {
        console.log('Initial load effect running');
        
        if (!hasFetchedInitialData.current) {
            console.log('Performing initial data fetch');
            hasFetchedInitialData.current = true;
            fetchPatients();
        }
        
        // Set initial date if not already set
        if (!initialDateSet.current) {
            initialDateSet.current = true;
        }
        
        // Cleanup function
        return () => {
            console.log('Cleaning up initial load effect');
        };
    }, [fetchPatients]);

    // Refetch when pagination changes (works for both search and non-search)
    useEffect(() => {
        if (hasFetchedInitialData.current) {
            fetchPatients();
        }
    }, [page, perPage, searchQuery, fetchPatients]);


    const handleDelete = (id) => {
        setDeleteId(id);
        setConfirmDelete(true);
    };

    const confirmDeletePatient = async () => {
        try {
                        await axios.delete(`${API_URL}patients/${deleteId}`, {
                headers: {
                    'Accept': 'application/json'
                }
            });
            setPatients(patients.filter(patient => patient.id !== deleteId));
            setDeleteId(null);
            setConfirmDelete(false);
        } catch (err) {
            setError('Error deleting patient: ' + (err.response?.data?.error || err.message));
            setDeleteId(null);
            setConfirmDelete(false);
        }
    };

    const cancelDelete = () => {
        setDeleteId(null);
        setConfirmDelete(false);
    };

    const toggleDropdown = (patientId) => {
        if (openDropdown === patientId) {
            setOpenDropdown(null);
        } else {
            setOpenDropdown(patientId);
            
            // Check if dropdown should appear above button to avoid cutoff
            setTimeout(() => {
                const dropdownElement = document.querySelector('.dropdown-menu-custom');
                if (dropdownElement) {
                    const rect = dropdownElement.getBoundingClientRect();
                    const viewportHeight = window.innerHeight;
                    
                    // If dropdown extends beyond viewport, add dropdown-up class
                    if (rect.bottom > viewportHeight - 20) {
                        dropdownElement.classList.add('dropdown-up');
                    } else {
                        dropdownElement.classList.remove('dropdown-up');
                    }
                }
            }, 0);
        }
    };

    const closeDropdown = () => {
        setOpenDropdown(null);
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = () => {
            closeDropdown();
        };
        
        if (openDropdown) {
            document.addEventListener('click', handleClickOutside);
        }
        
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [openDropdown]);

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container mt-4">
                <div className="alert alert-danger text-center">
                    {error}
                </div>
            </div>
        );
    }

    return (
        <div className="patient-list-container">
            <div className="container py-4">
            <div className="row justify-content-center">
                <div className="col-md-12">

                    <div className="card patient-list-card">
                        <div className="card-header patient-list-header d-flex justify-content-between align-items-center">
                            <h4>Patients</h4>
                            <div>
                                <button 
                                    className="btn create-patient-btn"
                                    onClick={() => navigate('/control/patient-create')}
                                >
                                    <i className="fas fa-plus me-2"></i>Create Patient
                                </button>
                            </div>
                        </div>
                        <div className="card-body patient-list-body">
                            <div className="search-filters-section">
                                <div className="row mb-3 g-2">
                                    <div className="col-md-12">
                                        <div className="input-group search-input-group">
                                            <input
                                                type="text"
                                                className="form-control search-input"
                                                placeholder="Search patients..."
                                                value={searchQuery}
                                                onChange={handleSearchInputChange}
                                                onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                                            />
                                            {isSearching ? (
                                                <span className="btn search-btn" disabled>
                                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                    <span className="visually-hidden">Searching...</span>
                                                </span>
                                            ) : (
                                                <button 
                                                    className="btn search-btn" 
                                                    type="button"
                                                    onClick={() => { setPage(1); setSearchQuery(''); }}
                                                    disabled={!searchQuery}
                                                    title={searchQuery ? 'Clear search' : 'Search'}
                                                >
                                                    {searchQuery ? (
                                                        <i className="fas fa-times"></i>
                                                    ) : (
                                                        <i className="fas fa-search"></i>
                                                    )}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="table-responsive patients-table-container">
                                <table className="table table-hover patients-table">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Name</th>
                                            <th>Phone</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {patients.length === 0 ? (
                                            <tr>
                                                <td colSpan="4" className="no-patients-state">
                                                    <div className="d-flex flex-column align-items-center">
                                                        <i className="fas fa-search no-patients-icon"></i>
                                                        <h5 className="no-patients-title">No patients found</h5>
                                                        <p className="no-patients-text">
                                                            {searchQuery ? (
                                                                <span>Try adjusting your search</span>
                                                            ) : (
                                                                <span>No patients in the system yet</span>
                                                            )}
                                                        </p>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : (
                                            patients.map((patient, index) => (
                                                <tr key={patient.id} className={openDropdown === patient.id ? 'dropdown-open' : ''}>
                                                    <td>{(page - 1) * perPage + index + 1}</td>
                                                    <td>
                                                        <div>
                                                            <Link 
                                                                to={`/control/patient/${patient.id}`}
                                                                className="patient-name-link"
                                                            >
                                                                {patient.name}
                                                            </Link>
                                                            <div className="text-muted" style={{fontSize: '0.65rem', marginTop: '2px'}}>
                                                                {(() => {
                                                                    // Convert UTC timestamp to local time
                                                                    if (patient.created_at) {
                                                                        const utcDate = new Date(patient.created_at); // Add Z to ensure UTC parsing
                                                                        return utcDate.toLocaleString('en-US', {
                                                                            year: 'numeric',
                                                                            month: 'short',
                                                                            day: 'numeric',
                                                                            hour: '2-digit',
                                                                            minute: '2-digit'
                                                                        });
                                                                    }
                                                                    return '';
                                                                })()}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>{patient.phone_number || '-'}</td>
                                                    <td>
                                                        <div className="dropdown-container">
                                                            <button 
                                                                className="btn action-menu-btn"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    toggleDropdown(patient.id);
                                                                }}
                                                                title="More actions"
                                                                aria-label="More actions"
                                                            >
                                                                <i className="fas fa-ellipsis-v"></i>
                                                            </button>
                                                            {openDropdown === patient.id && (
                                                                <div className="dropdown-menu-custom" onClick={(e) => e.stopPropagation()}>
                                                                    <button 
                                                                        className="dropdown-item-custom delete-item"
                                                                        onClick={() => {
                                                                            handleDelete(patient.id);
                                                                            closeDropdown();
                                                                        }}
                                                                    >
                                                                        <i className="fas fa-trash me-2"></i>
                                                                        Delete
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            <div className="d-flex flex-column flex-md-row align-items-center justify-content-between mt-3 gap-2">
                                <div className="text-muted small">
                                    {patients.length > 0 ? (
                                        <span>
                                            Showing {(page - 1) * perPage + 1}
                                            {' '}-{' '}
                                            {Math.min(page * perPage, total)} of {total}
                                        </span>
                                    ) : (
                                        <span>Showing 0 of {total}</span>
                                    )}
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    <label className="me-2 small text-muted">Rows per page</label>
                                    <select
                                      className="form-select form-select-sm"
                                      style={{ width: 'auto' }}
                                      value={perPage}
                                      onChange={(e) => { setPage(1); setPerPage(parseInt(e.target.value, 10)); }}
                                    >
                                      <option value={10}>10</option>
                                      <option value={20}>20</option>
                                      <option value={50}>50</option>
                                    </select>
                                    <div className="btn-group">
                                      <button
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => setPage(p => Math.max(1, p - 1))}
                                        disabled={page <= 1}
                                      >
                                        <i className="fas fa-chevron-left me-1"></i>
                                        Prev
                                      </button>
                                      <button
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => setPage(p => Math.min(lastPage, p + 1))}
                                        disabled={page >= lastPage}
                                      >
                                        Next
                                        <i className="fas fa-chevron-right ms-1"></i>
                                      </button>
                                    </div>
                                    <span className="small text-muted ms-2">Page {page} of {lastPage}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {confirmDelete && (
                <div className="modal show" style={{ display: 'block' }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Confirm Delete</h5>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={cancelDelete}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <p>Are you sure you want to delete this patient?</p>
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary" 
                                    onClick={cancelDelete}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-danger" 
                                    onClick={confirmDeletePatient}
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            </div>
        </div>
    )
}
